import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import GoogleLoginButton, { googleOAuthEnabled } from '../components/GoogleLoginButton';
import AppleLoginButton, { appleOAuthEnabled } from '../components/AppleLoginButton';
import Layout from '../components/Layout';
import OtpAuthFlow from '../components/auth/OtpAuthFlow';
import { storePhoneCountry } from '../components/auth/phoneCountryStorage';
import { userAPI } from '../services/api';
import { responseData } from '../services/apiResponse';
import { clearAuthenticatedUser, storeAuthenticatedUser } from '../services/authStorage';
import { trackEvent } from '../services/analytics';

export default function Login() {
	const navigate = useNavigate();
	const location = useLocation();
	const [pageError, setPageError] = useState('');
	const [checkingSession, setCheckingSession] = useState(Boolean(localStorage.getItem('token')));
	const allowedContacts = useMemo(() => ({
		email: { value: '', maskedValue: '', locked: false, requestBound: false },
		phone: { value: '', maskedValue: '', locked: false, requestBound: false },
	}), []);

	useEffect(() => {
		if (!localStorage.getItem('token')) return;
		userAPI.getCurrentUser().then(() => {
			navigate(location.state?.from?.pathname || '/', { replace: true });
		}).catch(() => {
			clearAuthenticatedUser();
			setCheckingSession(false);
		});
	}, [location.state, navigate]);

	const finishAuthentication = async (response, context = {}) => {
		if (context.method === 'phone' && context.country) {
			storePhoneCountry(localStorage, context.country);
		}
		storeAuthenticatedUser(responseData(response), context);
		trackEvent('login', { method: context.method || 'unknown' });
		navigate(location.state?.from?.pathname || '/', { replace: true });
	};

	const handleGoogleSuccess = async (tokenResponse) => {
		setPageError('');
		try {
			const response = await userAPI.socialLogin('google', tokenResponse.access_token);
			await finishAuthentication(response, { method: 'google' });
		} catch (error) {
			setPageError(error.response?.data?.detail || 'Google sign-in failed. Try again.');
		}
	};

	const handleAppleSuccess = async (authorization) => {
		setPageError('');
		try {
			const response = await userAPI.appleLogin(authorization);
			await finishAuthentication(response, { method: 'apple' });
		} catch (error) {
			setPageError(error.response?.data?.detail || 'Apple sign-in failed. Try again.');
		}
	};

	return (
		<Layout showHome={false}>
			<div className="mx-auto w-full max-w-md space-y-6">
				<header className="text-center">
					<div className="flex items-center justify-center gap-3">
						<img src="/koin-remit-icon.svg" alt="Koin Remit" className="h-12 w-12 shrink-0 object-contain" />
						<h1 className="text-2xl font-semibold text-slate-900">Sign into Koin Remit</h1>
					</div>
					<p className="mt-2 text-sm text-slate-600">Access your payments and transfer requests.</p>
				</header>

				{pageError && <div role="status" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{pageError}</div>}
				{checkingSession ? <p className="py-10 text-center text-sm text-slate-500">Checking your session...</p> : (
					<>
						{(googleOAuthEnabled || appleOAuthEnabled) && (
							<div className="space-y-3">
								{googleOAuthEnabled && <GoogleLoginButton onSuccess={handleGoogleSuccess} onError={() => setPageError('Google sign-in failed. Try again.')} className="flex h-12 w-full items-center justify-center rounded border border-slate-300 bg-white font-semibold text-slate-800 hover:bg-slate-50">Continue with Google</GoogleLoginButton>}
								{appleOAuthEnabled && <AppleLoginButton onSuccess={handleAppleSuccess} onError={() => setPageError('Apple sign-in failed. Try again.')} className="flex h-12 w-full items-center justify-center rounded bg-black font-semibold text-white hover:bg-slate-800">Continue with Apple</AppleLoginButton>}
								<div className="flex items-center gap-3 text-xs uppercase text-slate-400"><span className="h-px flex-1 bg-slate-200" /><span>or use a code</span><span className="h-px flex-1 bg-slate-200" /></div>
							</div>
						)}
						<OtpAuthFlow allowedContacts={allowedContacts} initialMethod="email" onAuthenticated={finishAuthentication} />
					</>
				)}

				<p className="text-center text-xs leading-5 text-slate-500">By continuing, you agree to our <Link to="/tos" className="text-cyan-800 underline">Terms of Service</Link> and <Link to="/privacy" className="text-cyan-800 underline">Privacy Policy</Link>.</p>
			</div>
		</Layout>
	);
}
